import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';

const PatientProfile: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('personal');

    // Mock patient profile data
    const profileData = {
        personal: {
            name: user?.displayName || 'John Doe',
            age: 28,
            pronouns: 'he/him',
            email: user?.email || 'john.doe@example.com',
            phone: '+1 (555) 123-4567',
            emergencyContact: 'Jane Doe - +1 (555) 987-6543'
        },
        therapy: {
            startDate: '2024-01-15',
            therapist: 'Dr. Sarah Smith',
            diagnosis: ['Generalized Anxiety Disorder', 'Mild Depression'],
            goals: ['Manage anxiety symptoms', 'Improve sleep quality', 'Build coping strategies'],
            progress: 75
        },
        preferences: {
            communicationStyle: 'Direct and supportive',
            sessionFrequency: 'Weekly',
            preferredTopics: ['Mindfulness', 'Cognitive Behavioral Therapy', 'Stress Management'],
            triggers: ['Work deadlines', 'Social situations', 'Financial stress']
        },
        progress: {
            totalSessions: 24,
            daysActive: 45,
            insightsGenerated: 12,
            memoryAnchors: 8,
            moodEntries: 32
        }
    };

    const tabs = [
        { id: 'personal', label: 'Personal Info', icon: '👤' },
        { id: 'therapy', label: 'Therapy Details', icon: '🛠️' },
        { id: 'preferences', label: 'Preferences', icon: '⚙️' },
        { id: 'progress', label: 'Progress', icon: '📈' }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Profile Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center space-x-6">
                        <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-3xl">👤</span>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
                            <p className="text-gray-600">Manage your personal information and therapy preferences</p>
                        </div>
                    </div>
                </div>

                {/* Profile Overview */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Overview</h2>
                    <div className="flex items-center space-x-6">
                        <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-3xl">👤</span>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">{profileData.personal.name}</h2>
                            <p className="text-gray-600">{profileData.personal.pronouns} • {profileData.personal.age} years old</p>
                            <p className="text-sm text-gray-500">{profileData.personal.email}</p>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Sections</h2>
                    <div className="flex flex-wrap gap-2 mb-6">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                    activeTab === tab.id
                                        ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <span>{tab.icon}</span>
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                {/* Tab Content */}
                {activeTab === 'personal' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Email:</span>
                                        <span className="font-medium">{profileData.personal.email}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Phone:</span>
                                        <span className="font-medium">{profileData.personal.phone}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Emergency Contact:</span>
                                        <span className="font-medium">{profileData.personal.emergencyContact}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-4">Personal Details</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Age:</span>
                                        <span className="font-medium">{profileData.personal.age} years</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Pronouns:</span>
                                        <span className="font-medium">{profileData.personal.pronouns}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={() => alert('Edit functionality coming soon!')}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                            Edit Personal Information
                        </button>
                    </div>
                )}

                {activeTab === 'therapy' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-4">Therapy Information</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Therapist:</span>
                                        <span className="font-medium">{profileData.therapy.therapist}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Start Date:</span>
                                        <span className="font-medium">{profileData.therapy.startDate}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Progress:</span>
                                        <span className="font-medium">{profileData.therapy.progress}%</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-4">Current Diagnosis</h3>
                                <div className="space-y-2">
                                    {profileData.therapy.diagnosis.map((diagnosis, index) => (
                                        <span key={index} className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm mr-2">
                                            {diagnosis}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-4">Therapy Goals</h3>
                            <div className="space-y-2">
                                {profileData.therapy.goals.map((goal, index) => (
                                    <div key={index} className="flex items-center space-x-2">
                                        <span className="text-green-500">✓</span>
                                        <span className="text-gray-700">{goal}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'preferences' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-4">Communication</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Style:</span>
                                        <span className="font-medium">{profileData.preferences.communicationStyle}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Frequency:</span>
                                        <span className="font-medium">{profileData.preferences.sessionFrequency}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-4">Preferred Topics</h3>
                                <div className="space-y-2">
                                    {profileData.preferences.preferredTopics.map((topic, index) => (
                                        <span key={index} className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm mr-2">
                                            {topic}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-4">Known Triggers</h3>
                            <div className="space-y-2">
                                {profileData.preferences.triggers.map((trigger, index) => (
                                    <span key={index} className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm mr-2">
                                        {trigger}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                            Update Preferences
                        </button>
                    </div>
                )}

                {activeTab === 'progress' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="text-center p-4 bg-blue-50 rounded-lg space-y-1">
                                <div className="text-3xl font-bold text-blue-600">{profileData.progress.totalSessions}</div>
                                <div className="text-sm text-gray-600">Total Sessions</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg space-y-1">
                                <div className="text-3xl font-bold text-green-600">{profileData.progress.daysActive}</div>
                                <div className="text-sm text-gray-600">Days Active</div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg space-y-1">
                                <div className="text-3xl font-bold text-purple-600">{profileData.progress.insightsGenerated}</div>
                                <div className="text-sm text-gray-600">Insights Generated</div>
                            </div>
                            <div className="text-center p-4 bg-orange-50 rounded-lg space-y-1">
                                <div className="text-3xl font-bold text-orange-600">{profileData.progress.moodEntries}</div>
                                <div className="text-sm text-gray-600">Mood Entries</div>
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-blue-600 mb-2">{profileData.therapy.progress}%</div>
                            <div className="text-lg text-gray-600">Overall Progress</div>
                            <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
                                <div 
                                    className="bg-blue-500 h-3 rounded-full" 
                                    style={{ width: `${profileData.therapy.progress}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default PatientProfile;
